import { CheckCircle2, MapPin, BadgeCheck } from "lucide-react";

const INNOCEAN_LOGO =
  "https://customer-assets.emergentagent.com/job_solmis-reunion/artifacts/s8j6lrrw_innoce%E2%95%A0%C3%BCanHD.jpg";
const INNOCEAN_URL = "https://www.innocean.re/";

const projects = [
  {
    src: "https://customer-assets.emergentagent.com/job_solmis-reunion/artifacts/nkc9qk32_ST%20PHILIPPE%20_OP%20MEUJELO%20PLAN%20REAL.png",
    commune: "Saint-Philippe",
    type: "Permis de construire",
    badge: "Accompagnement complet",
    alt: "Projet de maison individuelle à Saint-Philippe — permis de construire accompagné par Solmis Habitat",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_solmis-reunion/artifacts/839xebcm_PETITE%20ILE_BV_OP%20CATPAT%20VUE%20REAL%201.png",
    commune: "Petite-Île",
    type: "Maison contemporaine",
    badge: "Projet 2025",
    alt: "Projet de maison contemporaine à Petite-Île — étude et permis accompagnés par Solmis Habitat",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_solmis-reunion/artifacts/wueaq0xp_LE%20TAMPON%20OP%20SAMEDI%20PLAN%20REAL%202%20%281%29.png",
    commune: "Le Tampon",
    type: "Maison individuelle",
    badge: "Dossier réalisé",
    alt: "Projet de maison individuelle au Tampon — permis de construire accompagné par Solmis Habitat",
  },
];

const stats = [
  { n: "+100", label: "Projets réalisés" },
  { n: "974", label: "Toute l'île de La Réunion" },
  { n: "G1→G5", label: "Missions géotechniques" },
  { n: "1", label: "Interlocuteur unique" },
];

const proofs = [
  {
    icon: CheckCircle2,
    title: "+100 projets réalisés",
    desc: "Maisons individuelles, extensions, surélévations, locaux pro — un volume qui parle pour notre méthode.",
  },
  {
    icon: MapPin,
    title: "Intervention sur toute La Réunion",
    desc: "Côte Ouest, Sud, Est, Nord, et hauts inclus. Nous nous déplaçons partout sur l'île.",
  },
  {
    icon: BadgeCheck,
    title: "Études conformes aux exigences techniques",
    desc: "Missions géotechniques normées (G1 à G5), études SPANC et dossiers conformes aux règles d'urbanisme locales.",
  },
];

export default function Preuves() {
  return (
    <section
      id="preuves"
      data-testid="preuves-section"
      className="py-24 md:py-32"
    >
      <div className="container-wide">
        {/* Stats band */}
        <div className="grid grid-cols-2 md:grid-cols-4 border-y border-border divide-x divide-border reveal">
          {stats.map((s) => (
            <div key={s.label} className="px-4 py-8 md:py-10">
              <p className="font-display text-4xl md:text-5xl text-primary">
                {s.n}
              </p>
              <p className="mt-2 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                {s.label}
              </p>
            </div>
          ))}
        </div>

        {/* Heading */}
        <div className="mt-20 grid md:grid-cols-12 gap-10 items-end mb-12">
          <div className="md:col-span-7 reveal">
            <div className="flex items-center gap-3 mb-6">
              <span className="rule" />
              <span className="overline">Crédibilité</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-medium leading-tight text-primary">
              Une expertise terrain,{" "}
              <span className="italic font-light">éprouvée à La Réunion</span>.
            </h2>
          </div>
          <div className="md:col-span-5 reveal">
            <p className="text-base text-muted-foreground leading-relaxed">
              Nous ne publions pas d'avis clients fictifs. Nos preuves se
              mesurent autrement : volume de projets accompagnés, couverture
              de l'île, et qualité du réseau de partenaires.
            </p>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {proofs.map((p, i) => {
            const Icon = p.icon;
            return (
              <div
                key={p.title}
                data-testid={`preuve-${i}`}
                className="reveal border border-border bg-white p-8 hover:-translate-y-1 transition-all duration-300"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <Icon
                  size={22}
                  strokeWidth={1.5}
                  className="text-[hsl(var(--brand-terra))]"
                />
                <h3 className="mt-5 font-display text-xl text-primary leading-snug">
                  {p.title}
                </h3>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                  {p.desc}
                </p>
              </div>
            );
          })}
        </div>

        {/* Partnership block — Solmis Habitat × Innocean */}
        <div
          data-testid="partner-innocean"
          className="reveal mt-20 group border border-border overflow-hidden bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_60px_-24px_rgba(47,45,42,0.18)]"
        >
          <div className="grid md:grid-cols-12 gap-0">
            <div className="md:col-span-5 relative min-h-[320px] md:min-h-[420px] bg-white border-r border-border flex items-center justify-center p-4 md:p-6">
              <img
                src={INNOCEAN_LOGO}
                alt="Innocean — études de sol, assainissement et géotechnique à La Réunion"
                className="relative z-10 w-full h-full max-h-[380px] object-contain"
              />
            </div>
            <div className="md:col-span-7 p-10 md:p-14 relative flex flex-col">
              <p className="overline mb-4">Notre partenaire technique</p>
              <h3 className="font-display text-3xl md:text-4xl text-primary leading-tight">
                En partenariat avec{" "}
                <span className="italic font-light">Innocean</span>,
              </h3>
              <p className="mt-3 font-display text-xl md:text-2xl text-primary/80 leading-snug">
                expert en études de sol, assainissement et géotechnique à
                La Réunion.
              </p>

              <p className="mt-6 text-sm md:text-base text-muted-foreground leading-relaxed max-w-xl">
                Pour garantir un accompagnement technique fiable, Solmis
                Habitat s'appuie sur l'expertise terrain d'Innocean,
                spécialiste des études de sol, de la géotechnique et de
                l'assainissement non collectif à La Réunion.
              </p>

              <div className="mt-7 flex flex-wrap gap-2.5">
                {["Études de sol G1→G5", "Géotechnique", "SPANC", "Assainissement"].map(
                  (tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] border border-border rounded-full text-primary/80"
                    >
                      {tag}
                    </span>
                  )
                )}
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 pt-6 border-t border-border">
                <a
                  href="#contact"
                  data-testid="partner-cta-devis"
                  className="group/btn inline-flex items-center gap-2 bg-[hsl(var(--brand-terra))] text-white px-6 py-3.5 text-sm font-medium tracking-wide rounded-full transition-all duration-300 hover:-translate-y-0.5 hover:bg-[hsl(var(--brand-terra)/0.9)]"
                >
                  Demander mon devis
                  <span aria-hidden className="transition-transform duration-300 group-hover/btn:translate-x-1">→</span>
                </a>

                <a
                  href={INNOCEAN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="partner-link-innocean"
                  className="text-xs text-muted-foreground hover:text-primary underline-offset-4 underline transition-colors"
                >
                  En savoir plus sur Innocean ↗
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Project gallery — asymmetric premium layout */}
        <div data-testid="project-gallery" className="mt-20">
          <div className="grid md:grid-cols-12 gap-10 items-end mb-10">
            <div className="md:col-span-7 reveal">
              <div className="flex items-center gap-3 mb-6">
                <span className="rule" />
                <span className="overline">Photos de projets</span>
              </div>
              <h3 className="text-3xl sm:text-4xl font-medium leading-tight text-primary">
                Quelques projets{" "}
                <span className="italic font-light">accompagnés sur l'île</span>.
              </h3>
            </div>
            <div className="md:col-span-5 reveal">
              <p className="text-base text-muted-foreground leading-relaxed">
                Du Sud sauvage aux hauts du Tampon, en passant par Petite-Île —
                un aperçu de projets clés en main pilotés par Solmis Habitat.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6">
            {/* Featured large image */}
            <figure
              data-testid="project-card-0"
              className="reveal group relative md:col-span-7 md:row-span-2 overflow-hidden border border-border bg-[hsl(var(--brand-beige))]"
            >
              <div className="aspect-[4/3] md:aspect-auto md:h-[560px] overflow-hidden">
                <img
                  src={projects[0].src}
                  alt={projects[0].alt}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
                />
              </div>
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[hsl(var(--brand-charcoal)/0.78)] via-[hsl(var(--brand-charcoal)/0.18)] to-transparent" />
              {/* Top-left subtle badge */}
              <div className="absolute top-5 left-5 md:top-6 md:left-6">
                <span className="inline-flex items-center gap-2 px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] text-white bg-[hsl(var(--brand-charcoal)/0.55)] backdrop-blur-md border border-white/15 rounded-full">
                  <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--brand-terra))]" />
                  {projects[0].badge}
                </span>
              </div>
              <figcaption className="absolute inset-x-0 bottom-0 p-6 md:p-8 flex flex-wrap items-end justify-between gap-4">
                <div>
                  <span className="inline-flex items-center px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] bg-white/95 text-primary rounded-full">
                    {projects[0].type}
                  </span>
                  <p className="mt-3 font-display text-2xl md:text-3xl text-white leading-tight">
                    {projects[0].commune}
                  </p>
                </div>
                <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-white/85">
                  <MapPin size={13} strokeWidth={1.5} /> 974 · La Réunion
                </span>
              </figcaption>
            </figure>

            {/* Two stacked images */}
            {projects.slice(1).map((p, idx) => (
              <figure
                key={p.commune}
                data-testid={`project-card-${idx + 1}`}
                className="reveal group relative md:col-span-5 overflow-hidden border border-border bg-[hsl(var(--brand-beige))]"
                style={{ transitionDelay: `${(idx + 1) * 90}ms` }}
              >
                <div className="aspect-[4/3] md:aspect-auto md:h-[268px] overflow-hidden">
                  <img
                    src={p.src}
                    alt={p.alt}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.05]"
                  />
                </div>
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[hsl(var(--brand-charcoal)/0.78)] via-[hsl(var(--brand-charcoal)/0.15)] to-transparent" />
                {/* Top-left subtle badge */}
                <div className="absolute top-4 left-4 md:top-5 md:left-5">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[9.5px] uppercase tracking-[0.22em] text-white bg-[hsl(var(--brand-charcoal)/0.55)] backdrop-blur-md border border-white/15 rounded-full">
                    <span className="h-1 w-1 rounded-full bg-[hsl(var(--brand-terra))]" />
                    {p.badge}
                  </span>
                </div>
                <figcaption className="absolute inset-x-0 bottom-0 p-5 md:p-6 flex flex-wrap items-end justify-between gap-3">
                  <div>
                    <span className="inline-flex items-center px-2.5 py-1 text-[10px] uppercase tracking-[0.22em] bg-white/95 text-primary rounded-full">
                      {p.type}
                    </span>
                    <p className="mt-2 font-display text-xl md:text-2xl text-white leading-tight">
                      {p.commune}
                    </p>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>

          {/* Subtle footer line */}
          <div className="reveal mt-8 flex flex-wrap items-center justify-between gap-4 pt-6 border-t border-border">
            <p className="text-xs text-muted-foreground tracking-wide">
              Sélection non exhaustive — d'autres références disponibles sur demande.
            </p>
            <a
              href="#contact"
              data-testid="gallery-cta-devis"
              className="group/btn inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-[hsl(var(--brand-terra))] transition-colors"
            >
              Discuter de votre projet
              <span aria-hidden className="transition-transform duration-300 group-hover/btn:translate-x-1">→</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
